/**
 * Data Reliability v2 — lifecycle gating predicates.
 *
 * Per DEV-846 epic decision: each composite has an applicability predicate
 * based on project state. When inactive, the composite renders "N/A" with
 * the reason text — it does not contribute to any rollup.
 *
 * Three failure modes the OLD code had separate hacks for, all unified here:
 *   1. Completed projects (App.tsx:1688 hardcoded score=100; DataReliabilityTab
 *      lines 267-279 had its own remap to "reported")
 *   2. Brand-new projects (no override; just produced misleading 100% scores)
 *   3. Projects with no team configured (engagement signal silently 0)
 *
 * One source of truth for these gates is what makes the rest of the engine
 * trustworthy.
 *
 * See: DEV-846 epic, DEV-847.
 */

import type { DataReliabilityInputs } from './types';

/* ------------------------------------------------------------------------- */
/*  Constants                                                                */
/* ------------------------------------------------------------------------- */

/**
 * Outlook (leading) requires at least this many days of project history
 * before it can produce meaningful signal. Engagement, drift risk, and
 * entry latency trend all need accumulated activity to detect drift.
 *
 * Per DEV-846: 30 days. Revisit if production usage shows the threshold
 * is too tight or too loose.
 */
export const OUTLOOK_MIN_PROJECT_AGE_DAYS = 30;

/* ------------------------------------------------------------------------- */
/*  Helpers                                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Days between two ISO YYYY-MM-DD strings. Returns 0 for invalid inputs.
 * Positive when `b` is after `a`.
 */
function daysBetween(a: string, b: string): number {
  const da = Date.parse(a);
  const db = Date.parse(b);
  if (!Number.isFinite(da) || !Number.isFinite(db)) return 0;
  return Math.round((db - da) / (1000 * 60 * 60 * 24));
}

/**
 * Whether a project's status indicates it's a completed historical record
 * rather than an actively-managed work-in-flight. Reliability semantics
 * differ — a completed project's data is archive, not real-time decision
 * support, so the trust signal becomes meaningless.
 *
 * Status values come from the DB column directly. Comparison is
 * case-insensitive to defend against historical capitalization drift.
 */
function isCompletedStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return status.trim().toUpperCase() === 'COMPLETED';
}

/* ------------------------------------------------------------------------- */
/*  Predicates                                                               */
/* ------------------------------------------------------------------------- */

export interface ApplicabilityResult {
  applicable: boolean;
  reason?: string;
}

/**
 * Now (lagging) is applicable when:
 *   - Project is not completed (completed projects' data is archival)
 *   - Project has a configured start_date
 *   - At least one full day has elapsed since start_date — components like
 *     Completeness and Timeliness have nothing to measure on day 0.
 *
 * Per DEV-846 the original spec said "≥1 reporting period elapsed." Refined
 * here to "≥1 day" because a project can have its first reporting period
 * end on day 1 (daily cadence), or day 30 (monthly), and the engine doesn't
 * have visibility into cadence at this layer. The component-level
 * `Completeness` will return N/A when no reporting period has elapsed,
 * which is the right granularity for THAT signal — but we still want
 * Now applicable so OTHER components (Timeliness on entries that arrived
 * even before period close) can score.
 */
export function isNowApplicable(
  inputs: DataReliabilityInputs,
): ApplicabilityResult {
  if (isCompletedStatus(inputs.project_metadata.status)) {
    return {
      applicable: false,
      reason:
        'Project is completed — data reliability does not apply to historical archive',
    };
  }
  if (!inputs.project_metadata.start_date) {
    return {
      applicable: false,
      reason: 'Project start date not configured',
    };
  }
  const ageDays = daysBetween(
    inputs.project_metadata.start_date,
    inputs.as_of_date,
  );
  if (ageDays < 1) {
    return {
      applicable: false,
      reason: 'Project has not started yet',
    };
  }
  return { applicable: true };
}

/**
 * Outlook (leading) is applicable when:
 *   - Project is not completed (no future to forecast)
 *   - Project has been running for at least OUTLOOK_MIN_PROJECT_AGE_DAYS
 *     (engagement, drift risk, latency trend all need accumulated activity)
 *   - Project has at least one RACI assignment (engagement signal needs
 *     someone who's expected to acknowledge work)
 *
 * Skip's principle: don't penalize projects in setup. A 5-day-old project
 * with no team yet should show "N/A" for Outlook with a clear reason,
 * not a misleading 0% that suggests trouble.
 */
export function isOutlookApplicable(
  inputs: DataReliabilityInputs,
): ApplicabilityResult {
  if (isCompletedStatus(inputs.project_metadata.status)) {
    return {
      applicable: false,
      reason:
        'Project is completed — outlook does not apply to closed projects',
    };
  }
  if (!inputs.project_metadata.start_date) {
    return {
      applicable: false,
      reason: 'Project start date not configured',
    };
  }
  const ageDays = daysBetween(
    inputs.project_metadata.start_date,
    inputs.as_of_date,
  );
  if (ageDays < OUTLOOK_MIN_PROJECT_AGE_DAYS) {
    const remaining = OUTLOOK_MIN_PROJECT_AGE_DAYS - ageDays;
    return {
      applicable: false,
      reason: `Outlook needs at least ${OUTLOOK_MIN_PROJECT_AGE_DAYS} days of project history (${remaining} more day${remaining === 1 ? '' : 's'} to go)`,
    };
  }
  if (inputs.project_metadata.raci_assignment_count < 1) {
    return {
      applicable: false,
      reason: 'No RACI assignments configured — engagement signal unavailable',
    };
  }
  return { applicable: true };
}
